import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';

import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

interface RelatedCard {
  name: string;
  role: string;
  desc: string;
  img: string;
  imgAlt: string;
  internal: string;
  external: string;
}

export default function RelatedPage() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  const href = (path: string) => (lang === 'en' ? (path === '/' ? '/en' : `/en${path}`) : path);

  const cards: RelatedCard[] = [
    {
      name: t('related.hub.card.association.name'),
      role: t('related.hub.card.association.role'),
      desc: t('related.hub.card.association.desc'),
      img: '/images/generated/association-hero-2026.jpg',
      imgAlt: t('related.hub.card.association.imgAlt'),
      internal: '/related/butler-association',
      external: 'https://www.butler-association.jp/',
    },
    {
      name: t('related.hub.card.bell.name'),
      role: t('related.hub.card.bell.role'),
      desc: t('related.hub.card.bell.desc'),
      img: '/images/generated/news-hero-2026-x1.jpg',
      imgAlt: t('related.hub.card.bell.imgAlt'),
      internal: '/related/butler-bell',
      external: 'https://butlerbell.jp/',
    },
    {
      name: t('related.hub.card.butler.name'),
      role: t('related.hub.card.butler.role'),
      desc: t('related.hub.card.butler.desc'),
      img: '/images/generated/corporate-hotel.jpg',
      imgAlt: t('related.hub.card.butler.imgAlt'),
      internal: '/related/the-butler',
      external: 'https://thebutler.jp/',
    },
    {
      name: t('related.hub.card.fr.name'),
      role: t('related.hub.card.fr.role'),
      desc: t('related.hub.card.fr.desc'),
      img: '/images/related/family-romance/office.webp',
      imgAlt: t('related.hub.card.fr.imgAlt'),
      internal: '/related/family-romance',
      external: 'https://family-romance.com/',
    },
    {
      name: t('related.hub.card.ishii.name'),
      role: t('related.hub.card.ishii.role'),
      desc: t('related.hub.card.ishii.desc'),
      img: '/images/related/ishii-yuichi/profile-trenchcoat.jpg',
      imgAlt: t('related.hub.card.ishii.imgAlt'),
      internal: '/related/ishii-yuichi',
      external: 'https://getstage.com/',
    },
  ];

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('related.hub.hero.home'), item: 'https://butler-academy.jp/' },
      { '@type': 'ListItem', position: 2, name: t('related.hub.hero.about'), item: 'https://butler-academy.jp/about' },
      { '@type': 'ListItem', position: 3, name: t('related.hub.hero.current'), item: 'https://butler-academy.jp/related' },
    ],
  };

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('related.hub.hero.title'),
    description: t('related.hub.hero.lead'),
    url: 'https://butler-academy.jp/related',
    isPartOf: {
      '@type': 'WebSite',
      name: lang === 'en' ? 'Japan Butler Academy' : '日本執事アカデミー',
      url: 'https://butler-academy.jp/',
    },
    about: {
      '@type': 'Organization',
      name: lang === 'en' ? 'The Japan Butler Association' : '一般社団法人全日本執事協会',
      url: 'https://www.butler-association.jp/',
    },
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/related" jsonLd={[breadcrumbStructuredData, webPageStructuredData]} />

        {/* Hero */}
        <section className="hero-billboard relative w-full h-[60vh] min-h-[460px] md:h-[82vh] md:min-h-[640px] overflow-hidden">
          <img
            src="/images/generated/philosophy-hero-2026.jpg"
            alt={t('related.hub.hero.imgAlt')}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 hero-overlay"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="relative w-full px-8">
              <div className="max-w-[800px] mx-auto text-center">
                <div className="flex items-center justify-center gap-2 mb-8 text-[14px] tracking-[0.05em]">
                  <Link to={href('/')} className="text-foreground-200/70 hover:text-accent-400 transition-colors">
                    {t('related.hub.hero.home')}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <Link to={href('/about')} className="text-foreground-200/70 hover:text-accent-400 transition-colors">
                    {t('related.hub.hero.about')}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <span className="text-accent-400">{t('related.hub.hero.current')}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-50 tracking-[0.08em] mb-4">
                  {t('related.hub.hero.title')}
                </h1>
                {String(t('related.hub.hero.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-400 text-[15px] md:text-[17px] tracking-[0.12em] mb-6">{__s}</p>))}
                {String(t('related.hub.hero.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.05em] leading-relaxed max-w-[620px] mx-auto">{__s}</p>))}
              </div>
            </div>
          </div>
        </section>
      <Breadcrumb items={[{ label: '関連事業', labelEn: 'Related Businesses' }]} />

        {/* Intro */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-50">
            <div className="max-w-[860px] mx-auto px-8 text-center">
              {String(t('related.hub.intro.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                {t('related.hub.intro.title')}
              </h2>
              {String(t('related.hub.intro.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-5">{__s}</p>))}
              <div className="gold-line mx-auto mb-8"></div>
              {String(t('related.hub.intro.para1')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose mb-6 text-left">{__s}</p>))}
              {String(t('related.hub.intro.para2')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose text-left">{__s}</p>))}
            </div>
          </section>
        </FadeInSection>

        {/* Cards */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-100/50">
            <div className="max-w-[1200px] mx-auto px-8">
              <div className="text-center mb-14">
                {String(t('related.hub.cards.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  {t('related.hub.cards.title')}
                </h2>
                {String(t('related.hub.cards.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
                <div className="gold-line mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {cards.map((card, idx) => (
                  <div
                    key={idx}
                    className="card-glow group flex flex-col bg-background-50 border border-background-200/60 overflow-hidden"
                  >
                    <div className="relative h-[200px] overflow-hidden">
                      <img
                        src={card.img}
                        alt={card.imgAlt}
                        className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <span className="absolute top-4 left-4 bg-primary-900/85 text-accent-400 text-[12px] tracking-[0.1em] px-3 py-1">
                        {card.role}
                      </span>
                    </div>
                    <div className="flex flex-col flex-1 p-6">
                      <h3 className="font-heading text-[17px] text-foreground-900 tracking-[0.05em] mb-3 group-hover:text-accent-600 transition-colors">
                        {card.name}
                      </h3>
                      <p className="text-[14px] text-foreground-500 tracking-[0.03em] leading-relaxed mb-6 flex-1">
                        {card.desc}
                      </p>
                      <div className="flex flex-col gap-3 pt-4 border-t border-background-200/60">
                        <Link
                          to={href(card.internal)}
                          className="flex items-center gap-2 text-accent-600 hover:text-accent-500 transition-colors"
                        >
                          <span className="text-[14px] tracking-[0.05em]">{t('related.hub.card.viewInternal')}</span>
                          <i className="ri-arrow-right-line text-xs"></i>
                        </Link>
                        <a
                          href={card.external}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-foreground-400 hover:text-accent-500 transition-colors"
                        >
                          <span className="text-[13px] tracking-[0.05em]">{t('related.hub.card.viewExternal')}</span>
                          <i className="ri-external-link-line text-xs"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* Relationship */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-primary-900 text-foreground-200">
            <div className="max-w-[820px] mx-auto px-8 text-center">
              {String(t('related.hub.rel.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-400 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
              <h2 className="font-heading text-xl md:text-2xl text-foreground-50 tracking-[0.06em] mb-2">
                {t('related.hub.rel.title')}
              </h2>
              {String(t('related.hub.rel.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-400 text-[15px] tracking-[0.12em] mb-6">{__s}</p>))}
              <div className="gold-line mx-auto mb-8"></div>
              {String(t('related.hub.rel.body')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.03em] leading-loose">{__s}</p>))}
            </div>
          </section>
        </FadeInSection>

        {/* CTA */}
        <FadeInSection>
          <section className="relative py-20 md:py-24 bg-background-50">
            <div className="max-w-[760px] mx-auto px-8 text-center">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">
                {t('related.hub.cta.title')}
              </h2>
              {String(t('related.hub.cta.body')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-600 tracking-[0.03em] leading-relaxed mb-8">{__s}</p>))}
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  to={href('/courses')}
                  className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-primary-900 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  {t('related.hub.cta.courses')}
                </Link>
                <Link
                  to={href('/contact')}
                  className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  {t('related.hub.cta.contact')}
                </Link>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* Last updated */}
        <div className="bg-background-100 py-8">
          {String(t('related.hub.lastUpdated')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-center text-[13px] text-foreground-400 tracking-[0.05em]">{__s}</p>))}
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={t('related.common.backToTop')}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
